import { FC, useCallback } from 'react';
import {
  CloseFormShowButton,
  EditFormShowButton,
  SaveFormShowButton,
  useFormShowContext,
  TextInput,
  PhoneInput,
} from 'framework/form';
import { Grid2 as Grid, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getAnchorRowStyle, FormattedTextField, EmailField, PhoneField } from 'framework/ui/field';
import { defaultsDeep } from 'lodash';
import { Tooltip } from 'framework/ui/mui-builder';
import { useShowContext } from 'framework/show';
import RelativeDateWithTooltip from '../shared/RelativeDateWithTooltip';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
  ProjectSectionAccordion,
} from 'domain/shared/newProject';
import { EditableListDataGridLayout, EDITABLE_LIST_FIELD, EDITABLE_LIST_GROUP } from 'framework/list';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';

interface StakeholdersShowData {
  lastStakeholdersUpdatedAt?: string | null;
}

const truncatedText = (str: string, maxLength = 40) =>
  str?.length > maxLength ? (
    <Tooltip title={str}>
      <div>
        <Typography
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            wordBreak: 'break-word',
          }}
        >
          {str}
        </Typography>
      </div>
    </Tooltip>
  ) : (
    str || EMPTY_VALUE_INDICATOR
  );

const Stakeholders: FC = () => {
  const { isEditMode } = useFormShowContext();
  const { data: showData } = useShowContext<StakeholdersShowData>();
  const { lastStakeholdersUpdatedAt } = showData.getData();
  const { hash } = useLocation();
  const { isAccessGranted } = useProjectSecurityContext();
  const canManageStakeholders = isAccessGranted(ACCESS_INFO_SECTIONS.STAKEHOLDER, ACCESS_INFO_ACTIONS.MANAGE);

  const renderActions = useCallback(() => {
    if (!canManageStakeholders) {
      return null;
    }
    return isEditMode
      ? [<SaveFormShowButton key="save" />, <CloseFormShowButton key="close" />]
      : [<EditFormShowButton key="edit" />];
  }, [isEditMode, canManageStakeholders]);

  return (
    <ProjectSectionAccordion
      defaultExpanded
      title={
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            pl: 2,
            gap: { xs: 4, md: 16 },
          }}
        >
          <Grid size={2}>
            <Typography variant="h4" color="primary" noWrap>
              Stakeholders
            </Typography>
          </Grid>
          <Grid size="grow" display="flex" alignItems="end" justifyContent="end">
            {lastStakeholdersUpdatedAt ? (
              <Typography color="textSecondary" variant="caption" align="right">
                Last updated: <RelativeDateWithTooltip date={lastStakeholdersUpdatedAt} expandedUnits />
              </Typography>
            ) : (
              ''
            )}
          </Grid>
          <Grid size="auto" display="flex" alignItems="center">
            {renderActions()}
          </Grid>
        </Grid>
      }
    >
      <EditableListDataGridLayout
        rowProps={(data: any) =>
          defaultsDeep(getAnchorRowStyle(data, hash), {
            sx: {
              alignItems: isEditMode ? 'flex-start' : 'center',
              py: 2,
            },
          })
        }
        getRowEnabled={() => true}
        getTableCells={async (isEditModeLayout: boolean) => {
          if (isEditModeLayout) {
            return Promise.resolve([
              {
                type: EDITABLE_LIST_GROUP,
                component: Grid,
                componentProps: {
                  container: true,
                  alignItems: 'start',
                  spacing: 1,
                },
                gridCellProps: {
                  size: 11,
                  sx: {
                    '.floating-label-input': { mb: '0px!important' },
                    '.floating-label-baseInput': { mb: '0px!important' },
                    '.MuiFormControl-root': { mb: '0px!important' },
                    width: `calc(100% - 36px)`,
                  },
                },
                children: [
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'name',
                    isEditor: true,
                    component: TextInput,
                    componentProps: { label: 'Name' },
                    gridCellProps: { size: 3 },
                  },
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'jobTitle',
                    isEditor: true,
                    component: TextInput,
                    componentProps: { label: 'Job title' },
                    gridCellProps: { size: 3 },
                  },
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'email',
                    isEditor: true,
                    component: TextInput,
                    componentProps: { label: 'Email' },
                    gridCellProps: { size: 3 },
                  },
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'phone',
                    isEditor: true,
                    component: PhoneInput,
                    componentProps: { label: 'Phone' },
                    gridCellProps: { size: 3 },
                  },
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'note',
                    isEditor: true,
                    component: TextInput,
                    componentProps: { label: 'Note' },
                    gridCellProps: { size: 12 },
                  },
                ],
              },
            ]);
          }
          return Promise.resolve([
            {
              type: EDITABLE_LIST_FIELD,
              source: 'name',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                label: '',
                format: (str: string) => truncatedText(str),
              },
              gridCellProps: { size: 2 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'jobTitle',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                label: '',
                format: (str: string) => truncatedText(str),
              },
              gridCellProps: { size: 1.5 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'email',
              isEditor: false,
              component: EmailField,
              componentProps: {
                label: '',
              },
              gridCellProps: { size: 2 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'phone',
              isEditor: false,
              component: PhoneField,
              componentProps: {
                label: '',
              },
              gridCellProps: { size: 2 },
            },
            {
              type: EDITABLE_LIST_FIELD,
              source: 'note',
              isEditor: false,
              component: FormattedTextField,
              componentProps: {
                label: '',
                format: (str: string) => truncatedText(str, 30),
              },
              gridCellProps: { size: 4.5 },
            },
          ]);
        }}
        newRowDefaultData={{
          name: '',
          jobTitle: '',
          email: '',
          phone: '',
          note: '',
        }}
      />
    </ProjectSectionAccordion>
  );
};

export default Stakeholders;
